/**
 * EL RENDERIZADOR DE LA PROSA DE UN PASO — mínimo, acotado y a prueba de balas.
 *
 * Hector, la víspera del go-live: «¿en esos textos se pueden poner negritas? o
 * dar algo de formato? porque la verdad es pesado leerlos así». El caso de uso
 * es literal: párrafos largos que se leen DE PIE, con el reloj corriendo y a
 * las tres de la mañana. Lo bueno es que los textos de `steps.ts` ya están
 * escritos casi en markdown —acentos graves para el código, 🔴/⚠️/✅ para la
 * severidad, listas numeradas—, así que no hay que reescribirlos: hay que
 * RENDERIZARLOS.
 *
 * =============================================================================
 * 🔴 LO QUE ESTE FICHERO **NO** TOCA: LOS COMANDOS
 * =============================================================================
 *
 * Hector: «obvio, todos los comandos que vienen de terminal no necesitan
 * formato, sólo las prosas». Un `gcloud` al que alguien le cambia una comilla o
 * le parte una línea es un paso roto a las 3am, y el operador lo descubre
 * cuando lo pega.
 *
 * LA VALLA YA EXISTÍA Y ES UN CAMPO: `command`. No hay que inventar ningún
 * cercado dentro del texto ni marcar nada a mano — un paso ya declara por
 * separado su prosa (`detail`, `warning`) y su comando (`command`), y el front
 * ya los pinta en sitios distintos. Así que la regla es la más simple posible:
 *
 *   · `detail` y `warning`  ->  PROSA     -> pasan por aquí
 *   · `command`             ->  TERMINAL  -> NO pasa por aquí, jamás
 *
 * Y el sentido del fallo es el bueno: lo que no está declarado como comando se
 * trata como prosa. Si alguien mete un comando dentro de un `detail`, sale con
 * un `<code>` de más — feo, no roto. Al revés sí sería roto.
 *
 * =============================================================================
 * LA LISTA BLANCA, Y POR QUÉ ES UNA LISTA BLANCA
 * =============================================================================
 *
 * El texto se ESCAPA ENTERO primero y sólo después se le devuelven las cuatro
 * cosas que sabemos reconocer. Los textos los escribimos nosotros, así que hoy
 * no hay entrada de usuario — pero dentro de tres meses alguien pega un texto
 * con un `<` y eso no puede convertirse en un problema. Y ya hay `<` de verdad
 * en los textos: `<PROJECT>`, `<rango>_1x1.jpg`, `?username=<u>&force=true`.
 *
 *   `código`      ->  <code>     (lo que más ayuda: separa de un vistazo un
 *                                 fichero o un comando del texto que lo rodea)
 *   **negrita**   ->  <strong>
 *   🔴 / ⚠️ / ✅  ->  aviso con color de fondo tenue: es el semáforo del paso
 *   línea en blanco -> párrafo
 *
 * TODO LO DEMÁS SE QUEDA COMO ESTÁ, Y ESO ES DELIBERADO. En particular **no se
 * re-fluye NADA**: estos textos traen tablas alineadas a espacios
 *
 *     gtr-2026      la COPIA DE TRABAJO: sobre ella corren el barrido…
 *     37 avatares             img/gtr-images/avatars/{male,female,…}/   no
 *
 * que se leen por su alineación. El contenedor conserva `white-space: pre-wrap`
 * y cada carácter se queda donde el autor lo puso. Las listas se DETECTAN para
 * darles sangría francesa, no para recomponerlas.
 *
 * El orden con los marcadores de paso (`{{gate--c}}`) es UNO y está decidido en
 * el backend: allí se resuelven a su número y aquí llega TEXTO. Este fichero no
 * sabe nada de marcadores; si ve uno, es que algo se saltó `migrationStepsWithLabels`.
 */

/** Lo único que hace falta escapar al insertar en el CONTENIDO de un elemento. */
function escapeHtml(raw: string): string {
  return raw.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Las tres severidades que los textos ya usan. El emoji se queda dentro: es
 * parte del texto y quitarlo obligaría a mirar el color para saber qué es, que
 * es justo lo que no funciona con una pantalla mala y prisa.
 */
const SEVERITIES: { emoji: string; cls: string }[] = [
  { emoji: "🔴", cls: "sev-danger" },
  { emoji: "⚠️", cls: "sev-warn" },
  { emoji: "✅", cls: "sev-ok" },
];

/** Marcador de lista al principio de línea: `1.`, `2)`, `a)`, `·`, `•`. */
const LIST_RE = /^(\s*)(\d{1,2}[.)]|[a-z][.)]|[·•])\s+/;

function severityOf(block: string): string | undefined {
  const head = block.trimStart();
  return SEVERITIES.find((s) => head.startsWith(s.emoji))?.cls;
}

/**
 * Inline: primero el código, y NO por gusto. Los acentos graves protegen lo que
 * llevan dentro — en los textos de hoy hay `binary-tree/gtr.academy/**`,
 * `subscriptions/*` y un cron `0 11 1 * *`: si la negrita se aplicara antes,
 * esos asteriscos se comerían medio párrafo.
 */
function inline(escaped: string): string {
  const codes: string[] = [];
  // ⚠️ EL TESTIGO NO PUEDE OCUPAR SITIO. Uno tipo ` CODE0 ` metería DOS espacios
  // que no estaban en el texto, y en unos textos alineados a espacios eso es
  // corromper la tabla. `\u0001` no puede aparecer en el original (y si
  // apareciera, ya fue escapado) y desaparece antes de salir.
  const T = "\u0001";
  const conTestigos = escaped.replace(/`([^`\n]+)`/g, (_m, code: string) => {
    codes.push(code);
    return `${T}${codes.length - 1}${T}`;
  });
  // La negrita, DESPUÉS del código y sin cruzar líneas: un `**` suelto no puede
  // llevarse por delante medio párrafo.
  const conNegrita = conTestigos.replace(
    /\*\*([^*\n]+)\*\*/g,
    (_m, bold: string) => `<strong>${bold}</strong>`
  );
  return conNegrita.replace(
    new RegExp(`${T}(\\d+)${T}`, "g"),
    (_m, i: string) => `<code>${codes[Number(i)]}</code>`
  );
}

/**
 * UNA LÍNEA QUE SE LEE POR SU ALINEACIÓN, NO POR SU REDACCIÓN.
 *
 * El contenedor ya conservaba los espacios (`white-space: pre-wrap`), pero la
 * fuente es PROPORCIONAL: dos espacios no miden lo mismo en dos sitios
 * distintos, así que una tabla padeada a mano salía en escalera —la columna de
 * datos bailando línea a línea— justo en los pasos que existen para copiar
 * valores campo a campo.
 *
 * La firma es fiable en las dos direcciones: la prosa normal no lleva NUNCA dos
 * espacios seguidos entre palabras (una frase con doble espacio es un dedazo, no
 * una tabla), y una tabla alineada a espacios los lleva SIEMPRE. Por eso no hace
 * falta marcar las tablas a mano ni inventar una sintaxis: se reconocen solas, y
 * los ~30 pasos que ya traen tablas se arreglan sin tocar su texto.
 *
 * La segunda alternativa son las reglas —`—— binaryTree ——`— que separan una
 * tabla de la siguiente: sin ellas quedarían en otra fuente que la tabla que
 * encabezan, que es la misma escalera con otro disfraz.
 */
const ALINEADA_RE = /\S {2,}\S|[─—=]{3,}/;

/** Aplica el formato en línea y marca las líneas que se leen por su alineación. */
function porLineas(bloqueEscapado: string): string {
  return bloqueEscapado
    .split("\n")
    .map((linea) =>
      ALINEADA_RE.test(linea)
        ? `<span class="prose-aligned">${inline(linea)}</span>`
        : inline(linea)
    )
    .join("\n");
}

/**
 * Prosa de un paso -> HTML. SÓLO para `detail` y `warning`.
 *
 * Devuelve HTML que el llamador mete con `v-html`, y eso es seguro porque lo
 * único que llega ahí es lo que sale de `escapeHtml` más las cuatro etiquetas
 * de la lista blanca. No hay ninguna ruta por la que el texto de entrada pueda
 * aportar markup.
 */
export function renderStepProse(text: string | undefined): string {
  if (!text) return "";
  // Un bloque = lo que va entre dos líneas en blanco. Es como están escritos.
  const bloques = escapeHtml(text).split(/\n\s*\n/);

  return bloques
    .map((bloque) => {
      if (!bloque.trim()) return "";
      const sev = severityOf(bloque);
      const esLista = bloque.split("\n").some((l) => LIST_RE.test(l));
      const clases = ["prose-block", sev ?? "", esLista ? "prose-list" : ""]
        .filter(Boolean)
        .join(" ");
      return `<div class="${clases}">${porLineas(bloque)}</div>`;
    })
    .filter(Boolean)
    .join("");
}
